// İSG Portal — HTTP API client (ky).
//
// Sorumluluklar (brief 014):
//   1. Config-driven taban adres (env; `config.apiBaseUrl`).
//   2. Result envelope unwrap — başarıda `value`, hatada `ApiError` (api-contract §1).
//   3. Token interceptor — `Authorization: Bearer <access>` (auth-gated isteklere).
//   4. 401'de refresh rotasyonu — `/auth/refresh` (tek-uçuş); başarısızsa logout.
//
// `throwHttpErrors: false`: zarfı her durum kodunda okuruz (204/304 gibi bodyless
// yanıt sözleşmede yok — her yanıt zarf gövdelidir).

import ky, { type Options } from 'ky';
import { getAccessToken, getRefreshToken, useAuthStore } from '../auth/store';
import type { SupplierAuthResponseDto } from '../auth/supplier';
import type { TokenPairDto } from '../auth/types';
import { config } from '../config';
import type { Empty, PagedResult, ResultEnvelope } from './envelope';
import { ApiError, NETWORK_ERROR_CODE } from './errors';

const REFRESH_PATH = 'auth/refresh';

// `prefixUrl` ky ÖRNEĞİNE gömülmez — taban adres istek anında (lazy) okunup
// mutlak URL kurulur. Gerekçe: (1) `configureApiBaseUrl(...)` enjeksiyonu client
// kurulduktan sonra da geçerli olmalı; (2) mobilde (Metro) taban adres runtime'da
// enjekte edilir. `config.apiBaseUrl` bir getter'dır (config.ts).

/** Taban adres + göreli yol → mutlak URL (çift `/` olmadan). */
function buildUrl(path: string): string {
  return `${config.apiBaseUrl}/${path.replace(/^\/+/, '')}`;
}

/** Temel ky örneği — auth header `buildOptions` içinde eklenir (retry taze token okur). */
const http = ky.create({
  throwHttpErrors: false,
  retry: 0,
  timeout: 30_000,
});

/** Refresh çağrısı için hook'suz istemci (interceptor döngüsünü önler). */
const refreshHttp = ky.create({
  throwHttpErrors: false,
  retry: 0,
  timeout: 30_000,
});

interface RequestOptions {
  /** Auth-gated mı — access token ekle + 401'de refresh dene (varsayılan `true`). */
  auth?: boolean;
  /**
   * Açık Bearer token override (store yerine). Supplier çok-firma akışının transit
   * token'ı için kullanılır (`/auth/select-workspace`) — token store'a yazılmadan
   * yalnız bu istekte taşınır. Verildiğinde `auth: false` ile birlikte kullanılır
   * (transit refresh üretmez; api-contract §5.1).
   */
  bearer?: string;
  /**
   * `string[]` bir anahtarı **tekrarlanan** query param olarak yazar (§14.4 `?status=Active&
   * status=AwaitingEntryPermit` — S34-4, tekrarlanabilir `status`). Boş dizi hiçbir param
   * eklemez (tek değerli `undefined` ile aynı davranış — filtre uygulanmaz).
   */
  searchParams?: Record<string, string | number | boolean | string[] | undefined>;
  json?: unknown;
  /** İç kullanım: refresh sonrası tekrarda ikinci kez refresh denemeyi engeller. */
  _retried?: boolean;
  /**
   * TanStack Query'nin `queryFn({ signal })`ı — verilirse `ky`ye (dolayısıyla `fetch`e) AYNEN
   * geçer. İptal edilmiş (superseded) bir typeahead/arama isteği tarayıcı seviyesinde İPTAL
   * olur (security review, S34-5) — aksi hâlde her tuş vuruşu ayrı bir istek FIRE eder ve
   * yanıtlar YARIŞ hâlinde gelirse eski bir sorgunun sonucu daha yeni bir sorgunun ÜZERİNE
   * yazabilirdi (TanStack zaten sonucu sorgu anahtarına göre KORUR, ama ağ/sunucu yükünü YİNE
   * DE azaltır — additive, mevcut hiçbir çağıranı ETKİLEMEZ, hepsi `signal` GEÇMEZ).
   */
  signal?: AbortSignal;
}

// ---- Refresh rotasyonu (tek-uçuş / single-flight) ----
//
// Aynı anda birden çok istek 401 alırsa yalnız BİR refresh çağrısı yapılır; hepsi
// aynı promise'i bekler (refresh token reuse-detection'ı tetiklememek için — bkz.
// api-contract §5.3 eşzamanlı çift-kullanım yarışı).

let inflightRefresh: Promise<boolean> | null = null;

async function rotateRefreshToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  const res = await refreshHttp.post(buildUrl(REFRESH_PATH), { json: { refreshToken } });
  const env = (await safeJson(res)) as ResultEnvelope<
    TokenPairDto | SupplierAuthResponseDto
  > | null;

  if (!res.ok || !env?.isSuccess || !env.value) {
    return false;
  }

  const value = env.value;
  // Supplier terminal veya user token — ikisinde de accessToken var; refreshToken
  // user'da zorunlu, supplier terminal'de dolu. Transit (refresh yok) buraya düşmez.
  if (!value.accessToken || !value.refreshToken) {
    return false;
  }

  useAuthStore.getState().updateTokens({
    accessToken: value.accessToken,
    refreshToken: value.refreshToken,
  });
  return true;
}

function ensureRefreshed(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = rotateRefreshToken()
      .catch(() => false)
      .finally(() => {
        inflightRefresh = null;
      });
  }
  return inflightRefresh;
}

async function safeJson(res: Response): Promise<unknown | null> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function buildOptions(opts: RequestOptions): Options {
  const options: Options = {};
  if (opts.json !== undefined) {
    options.json = opts.json;
  }
  if (opts.searchParams) {
    // `URLSearchParams` (ky'nin `Record` yerine kabul ettiği alternatif) — dizi değerler
    // `.append` ile TEKRARLANAN anahtar üretir (`Record<string,string>` bunu ifade EDEMEZ,
    // aynı anahtarın son değeri öbürünü ezerdi).
    const params = new URLSearchParams();
    for (const [key, val] of Object.entries(opts.searchParams)) {
      if (val === undefined) {
        continue;
      }
      if (Array.isArray(val)) {
        for (const item of val) {
          params.append(key, item);
        }
      } else {
        params.append(key, String(val));
      }
    }
    options.searchParams = params;
  }
  if (opts.bearer) {
    // Açık token override (transit) — store token'ından önce gelir.
    options.headers = { Authorization: `Bearer ${opts.bearer}` };
  } else if (opts.auth !== false) {
    const token = getAccessToken();
    if (token) {
      options.headers = { Authorization: `Bearer ${token}` };
    }
  }
  if (opts.signal) {
    options.signal = opts.signal;
  }
  return options;
}

/**
 * Ortak istek yürütücü — HTTP çağrısı + zarf ayrıştırma + **401 refresh rotasyonu
 * (tek-uçuş) + tek tekrar**. Başarıda tam zarfı döner; hata zarfında `ApiError`
 * fırlatır. Hem tekil (`request`) hem liste (`requestList`) yardımcıları bunun
 * üzerine kurulur — böylece refresh single-flight yolu **liste okumasında da**
 * geçerlidir (web-supplier + iş modülleri birincil okuma yolu; api-contract §5.3).
 */
async function performRequest<T>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  path: string,
  opts: RequestOptions,
): Promise<ResultEnvelope<T>> {
  let res: Response;
  try {
    res = await http[method](buildUrl(path), buildOptions(opts));
  } catch (cause) {
    // Ağ/CORS/timeout — zarf üretilemedi.
    throw new ApiError({
      statusCode: 0,
      errorCode: NETWORK_ERROR_CODE,
      message: NETWORK_ERROR_CODE,
      messageData: { detail: cause instanceof Error ? cause.message : String(cause) },
    });
  }

  const env = (await safeJson(res)) as ResultEnvelope<T> | null;

  if (env?.isSuccess) {
    return env;
  }

  // 401 → refresh rotasyonu (yalnız auth-gated istek, refresh token varsa, tek tekrar).
  const refreshable =
    opts.auth !== false && !opts._retried && res.status === 401 && Boolean(getRefreshToken());

  if (refreshable) {
    const rotated = await ensureRefreshed();
    if (rotated) {
      return performRequest<T>(method, path, { ...opts, _retried: true });
    }
    // Refresh başarısız → oturumu düşür (istemci login'e yönlenir).
    useAuthStore.getState().clear();
  }

  throw new ApiError({
    statusCode: res.status,
    errorCode: env?.errorCode ?? `HTTP_${res.status}`,
    message: env?.message ?? env?.errorCode ?? `HTTP_${res.status}`,
    errors: env?.errors,
    messageData: env?.messageData,
  });
}

async function request<T>(
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  path: string,
  opts: RequestOptions,
): Promise<T> {
  const env = await performRequest<T>(method, path, opts);
  return (env.value ?? ({} as T)) as T;
}

/** İstemci-dostu liste yardımcısı — api-contract §3 (value + total/filteredTotal). */
async function requestList<TItem>(path: string, opts: RequestOptions): Promise<PagedResult<TItem>> {
  const env = await performRequest<TItem[]>('get', path, opts);
  return {
    items: env.value ?? [],
    total: env.total ?? 0,
    filteredTotal: env.filteredTotal ?? env.total ?? 0,
  };
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOptions, 'json'> = {}) => request<T>('get', path, opts),
  post: <T = Empty>(path: string, opts: RequestOptions = {}) => request<T>('post', path, opts),
  put: <T = Empty>(path: string, opts: RequestOptions = {}) => request<T>('put', path, opts),
  patch: <T = Empty>(path: string, opts: RequestOptions = {}) => request<T>('patch', path, opts),
  delete: <T = Empty>(path: string, opts: RequestOptions = {}) => request<T>('delete', path, opts),
  list: requestList,
  /**
   * `api.post` gibi ama `value`i değil TAM zarfı döner — yalnız HTTP durum kodunun kendisi
   * ANLAM taşıdığı uçlar için (§27.2 `POST .../versions`: aynı `200` hem "değişiklik yok" hem
   * "içerik var ama etkin değil" anlamına gelebilir, ayrımı `statusCode` + `value.isActive`
   * birlikte yapar — api-contract §27.2 "üç sonuç" kutusu). Olağan uçların HİÇBİRİ bunu
   * kullanmaz; `api.post` yeterlidir ve tercih edilir.
   */
  postEnvelope: <T>(path: string, opts: RequestOptions = {}) =>
    performRequest<T>('post', path, opts),
};
